'use client'

import React, { useId } from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import { Option, SelectField, SelectFieldProps } from '../SelectField'
import { Typography } from '../Typography'
import { Content } from './styles'

export interface SelectRangeFieldProps<
  T extends string | number | null | undefined,
> extends Omit<
      SelectFieldProps<T>,
      | 'value'
      | 'disabled'
      | 'errorMessages'
      | 'captionMessages'
      | 'placeholder'
      | 'onChange'
      | 'onBlur'
    >,
    SpaceProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Select label
   */
  label?: string

  /**
   * Range Value
   */
  value: {
    from?: T
    to?: T
  }

  /**
   * Required flag
   */
  required?: boolean

  /**
   * Left-side placeholder
   */
  fromPlaceholder?: string

  /**
   * Right-side placeholder
   */
  toPlaceholder?: string

  /**
   * Disable from select
   */
  fromDisabled?: boolean

  /**
   * Disable to select
   */
  toDisabled?: boolean

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption messages
   */
  captionMessages?: string | string[]

  /**
   * Disable body scroll lock
   */
  disableBodyScrollLock?: boolean

  /**
   * Override to option
   */
  toOptions?: Option<T>[]

  /**
   * onBlur handler (from)
   */
  onBlurFrom?: (e: React.FocusEvent) => void

  /**
   * onBlur handler (to)
   */
  onBlurTo?: (e: React.FocusEvent) => void

  /**
   * onChange handler
   */
  onChange: (value: { from?: T; to?: T }) => void
}

export const SelectRangeField = <T extends string | number | null | undefined>({
  id,
  className,
  label,
  required,
  autoWidth,
  placement = 'bottom',
  errorIcon,
  errorMessages,
  captionIcon,
  captionMessages,
  fromPlaceholder,
  toPlaceholder,
  fromDisabled,
  toDisabled,
  value,
  options,
  toOptions,
  onBlurFrom,
  onBlurTo,
  onChange,
  ...props
}: SelectRangeFieldProps<T>) => {
  const autoId = useId()
  const inputId = id ?? autoId
  const [spaceProps, selectProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content>
        <Content>
          <SelectField<T>
            id={`${inputId}-from`}
            autoWidth={autoWidth}
            placement={placement}
            placeholder={fromPlaceholder}
            options={options}
            value={value.from}
            onChange={(v) => onChange({ ...value, from: v })}
            onBlur={onBlurFrom}
            disabled={fromDisabled}
            {...selectProps}
          />
          <Typography as="div" variant="caption1" color="gray.01">
            〜
          </Typography>
          <SelectField<T>
            id={`${inputId}-to`}
            autoWidth={autoWidth}
            placement={placement}
            placeholder={toPlaceholder}
            options={toOptions ?? options}
            value={value.to}
            onChange={(v) => onChange({ ...value, to: v })}
            onBlur={onBlurTo}
            disabled={toDisabled}
            {...selectProps}
          />
        </Content>
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}
